import React from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Tabs,
  Notification,
  Message,
  IconWithText,
  FormikInput
} from '../../web.common';
import { sentenceCase } from 'change-case';
import CreatableArea from '../misc/CreatableArea';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import { providerTypes } from '../../enums/ProviderTypes';
import { getApiErrorMessage } from '../../common/utils';
import withApi from '../higherOrderComponents/withApi';
import { Divider, DetailsRightBar, NoLink } from './DetailsRightBar';
import { currencies } from '../../enums/Currencies';

class ProviderInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false
    };

    this.saveEditedProvider = this.saveEditedProvider.bind(this);
    this.isProviderPublic = this.isProviderPublic.bind(this);
  }

  async onValidationError({ validateForm }) {
    const validationErrors = await validateForm();
    const fieldsWithError = Object.keys(validationErrors).map(x => sentenceCase(x)).join(', ');
    this.setState({
      apiResponse: new Message('error', `Unable to save. Check fields: ${fieldsWithError}`)
    });
  }

  saveEditedProvider(receivedProps) {
    this.setState({ isLoading: true }, async () => {
      const {
        props: {
          language,
          history,
          api: {
            providers: {
              saveNewProviderData
            }
          }
        }
      } = this;

      let updatedProviderData = null;
      let apiResponse;

      try {
        updatedProviderData = await saveNewProviderData(receivedProps.values);
        apiResponse = new Message('success', 'Provider updated successfully');
      }
      catch (error) {
        apiResponse = getApiErrorMessage(error, language);

        this.setState({
          isLoading: false,
          apiResponse
        });

        return;
      }

      const { Id: providerId } = updatedProviderData;
      history.push(`/provider/${providerId}`);
    });
  }

  isProviderPublic(isPublic) {
    return (
      <IconWithText
        iconName={isPublic ? 'check' : 'times'}
        iconClasses={[`text-color-${isPublic ? 'success' : 'danger'}`]}
        text='Provider is public'
      />
    );
  }

  render() {
    const { isLoading, apiResponse } = this.state;

    const { categories } = this.props;
    const mappedCategories = categories.map(({ Name }) => ({ value: Name, label: Name }));
    const mappedProviderTypes = Object.entries(providerTypes)
      .map(([key]) => ({ value: key, label: key }));

    const Id = 0;
    const Name = '';
    const Description = '';
    const ContactName = '';
    const PhoneNumber = '';
    const Email = '';
    const Type = 'Undefined';
    const CreatedAt = '';
    const CurrencyIsoCode = 'DKK';
    const IsPublic = true;

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <Notification newMessage={apiResponse} />
            <div className='content-with-right-pane-delimiter'>
              <PageTop
                documentTitle='Provider page'
                documentDescription='Provider page'
              />

              <h2 className='main-page-header main-page-header-with-subheader'>
                Provider
              </h2>
              <h5 className='main-page-subheader'><i>New Provider</i></h5>
              <br />

              <CreatableArea
                ariaEditorName='editor-name'
                onSubmit={receivedProps => this.saveEditedProvider(receivedProps)}
                isLoading={isLoading}
                savingText='Updating...'
                displayButtonLabels
                onValidationError={formikProps => this.onValidationError(formikProps)}
                validationSchema={Yup.object().shape({
                  Name: Yup.string()
                    .trim()
                    .min(2, 'Should be at least 2 characters long')
                    .max(256, 'Should be less than 256 characters long')
                    .required('Field is Required'),
                  Description: Yup.string()
                    .trim()
                    .min(2, 'Should be at least 2 characters long')
                    .max(256, 'Should be less than 256 characters long')
                    .required('Field is Required'),
                  Email: Yup.string()
                    .trim()
                    .min(2, 'Should be at least 2 characters long')
                    .max(256, 'Should be less than 256 characters long')
                    .required('Field is Required'),
                  ContactName: Yup.string()
                    .trim()
                    .min(2, 'Should be at least 2 characters long')
                    .max(256, 'Should be less than 256 characters long')
                    .required('Field is Required'),
                  PhoneNumber: Yup.string()
                    .trim()
                    .min(2, 'Should be at least 2 characters long')
                    .max(256, 'Should be less than 256 characters long')
                    .required('Field is Required'),
                  Category: Yup.string().required('Field is Required')
                })}
                initialValues={{
                  Id,
                  Name,
                  ContactName,
                  PhoneNumber,
                  Email,
                  Description,
                  Type,
                  Category: [],
                  CreatedAt,
                  CurrencyIsoCode,
                  IsPublic
                }}
              >
                <React.Fragment>
                  <Tabs tablistAriaTitle='booking'>
                    {
                          [
                            {
                              tab: {
                                content: (<IconWithText iconName='info' text='Main Info' />)
                              },
                              panel: {
                                content: <DataDefinitionList
                                  classNames={['border-0', 'detail-page-list']}
                                  listData={[{
                                    'Name*': <FormikInput name='Name' description='Name' placeholder required />,
                                    'Description*': <FormikInput
                                      type='textarea'
                                      name='Description'
                                      description='Description'
                                      placeholder
                                    />,
                                    Type: <FormikInput
                                      type='select'
                                      options={mappedProviderTypes}
                                      name='Type'
                                      description='Type'
                                      placeholder
                                    />,
                                    'Category*': <FormikInput
                                      type='select'
                                      options={mappedCategories}
                                      isMulti
                                      name='Category'
                                      description='Category'
                                      placeholder
                                    />,
                                    'Currency Iso Code': <FormikInput
                                      type='select'
                                      options={currencies}
                                      name='CurrencyIsoCode'
                                      description='Currency Iso Code'
                                      placeholder
                                    />,
                                    'Is Public': <FormikInput
                                      type='checkbox'
                                      name='IsPublic'
                                      label='IsPublic'
                                      description='.'
                                      placeholder
                                    />
                                  }]}
                                  hasWideDescriptions
                                />
                              }
                            },
                            {
                              tab: {
                                content: (<IconWithText iconName='address-book' text='Contact' />)
                              },
                              panel: {
                                content: <DataDefinitionList
                                  classNames={['border-0', 'detail-page-list']}
                                  listData={[{
                                    'Email*': <FormikInput name='Email' description='Email' placeholder required />,
                                    'Contact Name*': <FormikInput
                                      name='ContactName'
                                      description='Contact Name'
                                      placeholder
                                      required
                                    />,
                                    'Phone Number*': <FormikInput
                                      name='PhoneNumber'
                                      description='Phone Number'
                                      placeholder
                                      required
                                    />
                                  }]}
                                  hasWideDescriptions
                                />
                              }
                            }
                          ]
                        }
                  </Tabs>
                </React.Fragment>
              </CreatableArea>
              <DetailsRightBar>
                <Divider>Links</Divider>
                <NoLink>None</NoLink>
                <Divider>Actions</Divider>
                <NoLink>None</NoLink>
              </DetailsRightBar>
            </div>
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}
const ProviderAdd = withRouter(withApi(
  connect(({ user, categories }) => ({ isLoggedIn: !!user, categories }), {})(ProviderInternal)
));

ProviderAdd.requiresLogin = true;
ProviderAdd.propTypes = { isLoggedIn: PropTypes.bool };

export default ProviderAdd;